import React, { useEffect, useState } from 'react';
import EArchiveListWrapper from '../EArchiveListWrapper/EArchiveListWrapper';
import EArchiveListFilter, {
	EArchiveListExcludeFilterFields,
	EArchiveListFilterValues,
} from '../EArchiveListFilter/EArchiveListFilter';
import EArchiveToolbar from '../EArchiveToolbar/EArchiveToolbar';
import EArchivePatientList from '../EArchivePatientList/EArchivePatientList';
import { useAppContext } from '../../../../Services/AppContext';
import { View } from '../../../../Enums/View.enum';
import {
	EArchiveWorkplacePatientsQuery,
	useEArchiveWorkplacePatientsQuery,
	WorkplacePatientStatus,
} from '../../../../graphql/graphql';
import { useSelectUsersContext } from '../../Services/SelectUsersContext';
import { useFlatDataList } from '../../../Base/Hooks/useFlatDataList';

interface Props {
	defaultFilter?: EArchiveListFilterValues;
	excludeFilterFields?: EArchiveListExcludeFilterFields;
}

type WorkplacePatient = NonNullable<
	NonNullable<EArchiveWorkplacePatientsQuery['workplacePatients']>['data']
>[0];

const EArchivePatientsListWrapper = ({
	defaultFilter = {},
	excludeFilterFields,
}: Props) => {
	const [view, setView] = useState(View.GRID);
	const [filter, setFilter] = useState<EArchiveListFilterValues>(defaultFilter);
	const { socket } = useAppContext();
	const { showSelectCheckbox } = useSelectUsersContext();
	const { flatData, pagination, getNextPage, appendData } =
		useFlatDataList<WorkplacePatient>(filter);
	const { data, isFetching, refetch } = useEArchiveWorkplacePatientsQuery({
		...filter,
		...pagination,
	});

	useEffect(() => {
		appendData(data?.workplacePatients?.data ?? []);
	}, [data]);

	useEffect(() => {
		refetch();
	}, [filter]);

	const handleFilterChange = (filterValues: EArchiveListFilterValues) => {
		setFilter({
			fulltext: filterValues.fulltext,
			doctorId: filterValues.doctorId,
			healthPlanIds: filterValues.healthPlanIds,
			insuranceCompanyIds: filterValues.insuranceCompanyIds,
			workplaceId: filterValues.workplaceId,
			status: filterValues.status as unknown as WorkplacePatientStatus,
			...defaultFilter,
		});
	};

	useEffect(() => {
		socket.on('refreshEArchive', refetch);
		return () => {
			socket.off('refreshEArchive', refetch);
		};
	}, [socket, refetch]);

	return (
		<EArchiveListWrapper>
			<EArchiveListFilter
				onFilter={handleFilterChange}
				excludeFilterFields={excludeFilterFields}
			/>
			{!showSelectCheckbox && (
				<EArchiveToolbar
					itemsCount={data?.workplacePatients?.meta?.count ?? 0}
					onViewChange={setView}
					activeView={view}
					reloadData={refetch}
				/>
			)}
			<EArchivePatientList
				loadData={refetch}
				getNextPage={getNextPage}
				data={flatData}
				isLoading={isFetching}
				hasMoreData={data?.workplacePatients?.meta?.hasNextPage ?? false}
				view={view}
			/>
		</EArchiveListWrapper>
	);
};

export default EArchivePatientsListWrapper;
